import React, { useState } from 'react';
import { Mail, Send, Inbox, Star, Trash2, Search, Plus, Paperclip, Archive, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const EmailPage = () => {
  const [emails, setEmails] = useState([
    {
      id: 1,
      from: 'john@acme.com',
      subject: 'Re: Website Redesign Project',
      preview: 'Thanks for the update. Everything looks great so far...',
      body: 'Thanks for the update. Everything looks great so far. I reviewed the mockups and they align perfectly with our vision. Can we schedule a call next week to discuss the implementation timeline?',
      date: '2 mins ago',
      read: false,
      starred: false,
    },
    {
      id: 2,
      from: 'billing@client.com',
      subject: 'Invoice #1234 Payment Received',
      preview: 'Payment of $50,000 has been processed...',
      body: 'Payment of $50,000 has been processed successfully. Thank you for your business. Your payment confirmation number is PAY-2024-001234.',
      date: '1 hour ago',
      read: true,
      starred: true,
    },
    {
      id: 3,
      from: 'sarah@techstart.io',
      subject: 'Meeting scheduled for next week',
      preview: 'Our meeting is set for Tuesday at 2 PM...',
      body: 'Our meeting is set for Tuesday at 2 PM. Looking forward to discussing the project details with you.',
      date: '3 hours ago',
      read: true,
      starred: false,
    },
  ]);

  const [selectedEmail, setSelectedEmail] = useState(null);
  const [composing, setComposing] = useState(false);
  const [composeData, setComposeData] = useState({ to: '', subject: '', body: '' });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleCompose = () => {
    setComposing(true);
    setSelectedEmail(null);
  };

  const handleSend = () => {
    toast.success('Email sent successfully!');
    setComposing(false);
    setComposeData({ to: '', subject: '', body: '' });
  };

  const handleStarToggle = (id) => {
    setEmails(emails.map(email => 
      email.id === id ? { ...email, starred: !email.starred } : email
    ));
  };

  return (
    <div className="relative h-screen flex flex-col bg-black overflow-hidden">
      {/* Hover trigger for sidebar */}
      <div 
        className="fixed top-0 right-0 w-4 h-full z-40"
        onMouseEnter={() => setSidebarOpen(true)}
      />

      {/* Auto-hiding Sidebar on Right */}
      <aside
        className={`fixed inset-y-0 right-0 z-50 w-96 bg-black border-l-2 border-[#C9A961] transform transition-all duration-300 ease-in-out shadow-2xl overflow-hidden flex flex-col ${
          sidebarOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onMouseEnter={() => setSidebarOpen(true)}
        onMouseLeave={() => setSidebarOpen(false)}
      >
        {/* Search Bar at Top */}
        <div className="p-6 border-b border-[#C9A961]/30 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-[#C9A961]">Inbox</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
              className="text-[#C9A961] hover:bg-[#C9A961]/20"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
            <Input 
              placeholder="Search emails..." 
              className="pl-10 bg-black/50 text-white border-0 focus:ring-2 focus:ring-[#C9A961]"
            />
          </div>
        </div>
        
        {/* Email List in Middle */}
        <div className="flex-1 overflow-y-auto">
          {emails.map((email) => (
            <div
              key={email.id}
              onClick={() => { 
                setSelectedEmail(email); 
                setComposing(false);
                setEmails(emails.map(e => e.id === email.id ? { ...e, read: true } : e));
                setSidebarOpen(false);
              }}
              className={`p-4 cursor-pointer border-b border-[#C9A961]/10 hover:bg-[#C9A961]/10 transition-all ${
                selectedEmail?.id === email.id ? 'bg-[#C9A961]/20 border-l-4 border-l-[#C9A961]' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 flex-1">
                  <p className={`text-sm font-medium truncate ${
                    email.read ? 'text-gray-400' : 'text-white font-bold'
                  }`}>
                    {email.from}
                  </p>
                  {!email.read && (
                    <Badge className="h-2 w-2 p-0 rounded-full bg-[#C9A961] flex-shrink-0" />
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStarToggle(email.id);
                  }}
                  className="text-[#C9A961] hover:bg-[#C9A961]/20 flex-shrink-0"
                >
                  <Star className={`h-3 w-3 ${email.starred ? 'fill-[#C9A961]' : ''}`} />
                </Button>
              </div>
              <p className="text-sm font-semibold text-white mb-1 truncate">{email.subject}</p>
              <p className="text-xs text-gray-400 line-clamp-2">{email.preview}</p>
              <p className="text-xs text-gray-600 mt-1">{email.date}</p>
            </div>
          ))}
        </div>

        {/* Navigation at Bottom */}
        <div className="p-4 border-t border-[#C9A961]/30 flex-shrink-0">
          <div className="space-y-1">
            <Button variant="ghost" className="w-full justify-start text-[#C9A961] hover:bg-[#C9A961]/20 text-sm">
              <Inbox className="mr-2 h-4 w-4" />
              Inbox ({emails.length})
            </Button>
            <Button variant="ghost" className="w-full justify-start text-[#C9A961] hover:bg-[#C9A961]/20 text-sm">
              <Star className="mr-2 h-4 w-4" />
              Starred ({emails.filter(e => e.starred).length})
            </Button>
            <Button variant="ghost" className="w-full justify-start text-[#C9A961] hover:bg-[#C9A961]/20 text-sm">
              <Send className="mr-2 h-4 w-4" />
              Sent
            </Button>
            <Button variant="ghost" className="w-full justify-start text-[#C9A961] hover:bg-[#C9A961]/20 text-sm">
              <Archive className="mr-2 h-4 w-4" />
              Archive
            </Button>
            <Button variant="ghost" className="w-full justify-start text-[#C9A961] hover:bg-[#C9A961]/20 text-sm">
              <Trash2 className="mr-2 h-4 w-4" />
              Trash
            </Button>
          </div>
        </div>
      </aside>

      {/* Header - Full Width */}
      <div className="p-8 border-b-2 border-[#C9A961] flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <img src="/williams-logo-transparent.png" alt="Williams Diversified" className="h-24" />
            <div>
              <h1 className="text-4xl font-bold text-[#C9A961] mb-2">Business Email</h1>
              <p className="text-gray-400 text-lg">Integrated Gmail - admin@williamsdiverse.com</p>
            </div>
          </div>
          <Button 
            onClick={handleCompose}
            className="bg-[#C9A961] text-black hover:bg-[#D4C278] hover:scale-105 transition-all text-lg px-6 py-6"
          >
            <Plus className="mr-2 h-5 w-5" />
            Compose
          </Button>
        </div>
      </div>

      {/* Main Content - Full Width */}
      <div className="flex-1 overflow-y-auto bg-black">
        {composing ? (
          <div className="p-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-[#C9A961] mb-8">New Message</h2>
              <div className="space-y-6">
                <div>
                  <label className="text-lg font-semibold text-[#C9A961] mb-2 block">To:</label>
                  <Input
                    value={composeData.to}
                    onChange={(e) => setComposeData({ ...composeData, to: e.target.value })}
                    placeholder="recipient@email.com"
                    className="bg-black/50 border-0 text-white text-lg py-6 focus:ring-2 focus:ring-[#C9A961]"
                  />
                </div>
                <div>
                  <label className="text-lg font-semibold text-[#C9A961] mb-2 block">Subject:</label>
                  <Input
                    value={composeData.subject}
                    onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                    placeholder="Email subject"
                    className="bg-black/50 border-0 text-white text-lg py-6 focus:ring-2 focus:ring-[#C9A961]"
                  />
                </div>
                <div>
                  <label className="text-lg font-semibold text-[#C9A961] mb-2 block">Message:</label>
                  <Textarea
                    value={composeData.body}
                    onChange={(e) => setComposeData({ ...composeData, body: e.target.value })}
                    placeholder="Write your message..."
                    rows={16}
                    className="bg-black/50 border-0 text-white text-lg focus:ring-2 focus:ring-[#C9A961]"
                  />
                </div>
                <div className="flex gap-4 pt-4">
                  <Button 
                    onClick={handleSend}
                    className="bg-[#C9A961] text-black hover:bg-[#D4C278] hover:scale-105 transition-all text-lg px-8 py-6"
                  >
                    <Send className="mr-2 h-5 w-5" />
                    Send Email
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setComposing(false)}
                    className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10 text-lg px-8 py-6"
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="outline"
                    className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10 text-lg px-6 py-6"
                  >
                    <Paperclip className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : selectedEmail ? (
          <div className="p-8">
            <div className="max-w-4xl mx-auto">
              <div className="mb-8 pb-6 border-b border-[#C9A961]/30">
                <h2 className="text-3xl font-bold text-white mb-4">{selectedEmail.subject}</h2>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xl font-semibold text-[#C9A961]">{selectedEmail.from}</p>
                    <p className="text-sm text-gray-400 mt-1">{selectedEmail.date}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10"
                    >
                      <Archive className="h-4 w-4 mr-2" />
                      Archive
                    </Button>
                    <Button 
                      variant="outline"
                      className="border-red-500 text-red-500 hover:bg-red-950"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
              <div className="mb-8">
                <p className="text-lg text-white leading-relaxed whitespace-pre-wrap">
                  {selectedEmail.body}
                </p>
              </div>
              <div className="pt-6 border-t border-[#C9A961]/30">
                <Button 
                  onClick={() => {
                    setComposing(true);
                    setComposeData({
                      to: selectedEmail.from,
                      subject: `Re: ${selectedEmail.subject}`,
                      body: ''
                    });
                  }}
                  className="bg-[#C9A961] text-black hover:bg-[#D4C278] hover:scale-105 transition-all text-lg px-8 py-6"
                >
                  <Send className="mr-2 h-5 w-5" />
                  Reply
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Mail className="h-24 w-24 text-[#C9A961]/30 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-400 mb-2">No email selected</h3>
              <p className="text-gray-500">Choose an email from the list to read</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailPage;